import React, { useEffect, useState } from "react";
import {
  CheckCircleOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DislikeOutlined,
  ExclamationCircleOutlined,
  LikeOutlined,
  SendOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Slider,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { message } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// AI Models Configuration - Only ChatGPT and Gemini
const AI_PROVIDERS = {
  openai: {
    name: "ChatGPT",
    models: [
      { id: "gpt-4o", name: "GPT-4o", description: "Most advanced multimodal model" },
      { id: "gpt-4o-mini", name: "GPT-4o Mini", description: "Fast and cost-effective" },
      { id: "gpt-4-turbo", name: "GPT-4 Turbo", description: "High performance general purpose" },
      { id: "gpt-4", name: "GPT-4", description: "Most capable GPT-4 model" },
      { id: "gpt-3.5-turbo", name: "GPT-3.5 Turbo", description: "Fast and versatile" },
    ],
  },
  google: {
    name: "Google Gemini",
    models: [
      { id: "gemini-1.5-pro", name: "Gemini 1.5 Pro", description: "Most capable Gemini model" },
      { id: "gemini-1.5-flash", name: "Gemini 1.5 Flash", description: "Fast and efficient" },
      { id: "gemini-1.0-pro", name: "Gemini 1.0 Pro", description: "Balanced performance model" },
    ],
  },
};

// Resume Data Fields Configuration
const RESUME_DATA_FIELDS = [
  { id: "personalInfo", name: "Personal Information", description: "Name, email, phone, location, etc." },
  { id: "summary", name: "Summary", description: "Professional summary or objective" },
  { id: "experience", name: "Work Experience", description: "Professional work history" },
  { id: "education", name: "Education", description: "Educational background" },
  { id: "skills", name: "Skills", description: "Technical and soft skills" },
  { id: "projects", name: "Projects", description: "Notable projects and achievements" },
  { id: "certifications", name: "Certificates", description: "Professional certifications" },
  { id: "languages", name: "Languages", description: "Language proficiencies and fluency levels" },
  { id: "awards", name: "Awards", description: "Recognition, honors, and achievements" },
  { id: "publications", name: "Publications", description: "Published works, papers, and articles" },
  { id: "references", name: "References", description: "Professional references and recommendations" },
  { id: "fullData", name: "Complete Resume Data", description: "All resume information in structured format" },
];

// Analysis Display Component - Reuses the chat interface tooltip design
const AnalysisDisplay = ({ score, goodPoints, badPoints, suggestions }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return "#52c41a"; // Green
    if (score >= 60) return "#faad14"; // Orange
    return "#ff4d4f"; // Red
  };

  if (score === null && (!goodPoints || goodPoints.length === 0) && (!badPoints || badPoints.length === 0)) {
    return null;
  }

  return (
    <div
      style={{
        width: "100%",
        padding: "12px",
        fontSize: "12px",
        border: "1px solid #f0f0f0",
        borderRadius: "6px",
        backgroundColor: "#fafafa",
        minHeight: "auto",
        height: "auto",
      }}
    >
      {/* Score Section - Compact Header */}
      {score !== null && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "12px",
            padding: "8px 12px",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px",
            borderLeft: `4px solid ${getScoreColor(score)}`,
          }}
        >
          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              color: getScoreColor(score),
              marginRight: "8px",
            }}
          >
            {score}/100
          </div>
          <div style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Score</div>
        </div>
      )}

      {/* Two-Column Layout for Sections */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          marginBottom: "12px",
        }}
      >
        {/* Left Column - Strengths */}
        {goodPoints && goodPoints.length > 0 && (
          <div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#52c41a",
                marginBottom: "8px",
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: "4px",
              }}
            >
              ✅ Strengths
            </div>
            <div style={{ fontSize: "13px", color: "#333", lineHeight: "1.4" }}>
              {goodPoints.map((item, index) => (
                <div key={index} style={{ marginBottom: "6px" }}>
                  • {item}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Right Column - Improvements */}
        {badPoints && badPoints.length > 0 && (
          <div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#faad14",
                marginBottom: "8px",
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: "4px",
              }}
            >
              ⚠️ Improvements
            </div>
            <div style={{ fontSize: "13px", color: "#333", lineHeight: "1.4" }}>
              {badPoints.map((item, index) => (
                <div key={index} style={{ marginBottom: "6px" }}>
                  • {item}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Full Width - Suggestions */}
      {suggestions && suggestions.length > 0 && (
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: "8px",
            marginTop: "8px",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#1890ff",
              marginBottom: "8px",
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: "4px",
            }}
          >
            💡 AI Suggestions
          </div>
          <div style={{ fontSize: "13px", color: "#333", lineHeight: "1.4" }}>
            {suggestions.map((item, index) => (
              <div key={index} style={{ marginBottom: "6px" }}>
                • {item}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AIPromptTestingModal = ({ visible, onCancel, resumeData }) => {
  const fetch = useAuthenticatedFetch();
  const [form] = Form.useForm();

  // Get current resume ID to track state per resume
  const currentResumeId = resumeData && resumeData.length > 0 ? resumeData[0].resumeId : null;

  // Form state - Reset when resume changes
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("openai");
  const [selectedModel, setSelectedModel] = useState("gpt-4o-mini");
  const [temperature, setTemperature] = useState(0.3);
  const [maxTokens, setMaxTokens] = useState(4000);
  const [selectedDataField, setSelectedDataField] = useState("personalInfo");
  const [selectedSpecificField, setSelectedSpecificField] = useState(null);

  // Execution state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [executionTime, setExecutionTime] = useState(null);
  const [selectedResumeData, setSelectedResumeData] = useState(null);
  const [lastResumeId, setLastResumeId] = useState(null);

  // Rating system state
  const [responseRating, setResponseRating] = useState(null);
  const [goodPoints, setGoodPoints] = useState([]);
  const [badPoints, setBadPoints] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  // Reset state when resume changes to prevent cross-contamination
  useEffect(() => {
    if (currentResumeId && currentResumeId !== lastResumeId) {
      // Reset all state for new resume
      setSystemPrompt("");
      setUserPrompt("");
      setSelectedProvider("openai");
      setSelectedModel("gpt-4o-mini");
      setTemperature(0.3);
      setMaxTokens(4000);
      setSelectedDataField("personalInfo");
      setSelectedSpecificField(null);
      setTestResult(null);
      setExecutionTime(null);
      setSelectedResumeData(null);
      setResponseRating(null);
      setGoodPoints([]);
      setBadPoints([]);
      setSuggestions([]);
      setLastResumeId(currentResumeId);
      form.resetFields();
    }
  }, [currentResumeId, lastResumeId, form]);

  // Update model when provider changes (but preserve gpt-4o-mini default for OpenAI)
  useEffect(() => {
    if (selectedProvider && AI_PROVIDERS[selectedProvider]) {
      if (selectedProvider === "openai") {
        // Keep gpt-4o-mini as default for OpenAI
        setSelectedModel("gpt-4o-mini");
      } else {
        const firstModel = AI_PROVIDERS[selectedProvider].models[0];
        setSelectedModel(firstModel.id);
      }
    }
  }, [selectedProvider]);

  // Helper function to get available specific fields for array data
  const getSpecificFields = (dataField, data) => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    switch (dataField) {
      case "experience":
        return data.map((exp, index) => ({
          key: index,
          label: `${exp.company || "Unknown Company"} - ${exp.position || "Unknown Position"}${
            exp.startDate ? ` (${exp.startDate})` : ""
          }`,
        }));

      case "education":
        return data.map((edu, index) => ({
          key: index,
          label: `${edu.institution || edu.school || "Unknown Institution"} - ${edu.degree || "Unknown Degree"}${
            edu.startDate ? ` (${edu.startDate})` : ""
          }`,
        }));

      case "projects":
        return data.map((project, index) => ({
          key: index,
          label: `${project.name || project.title || `Project ${index + 1}`}${
            project.startDate ? ` (${project.startDate})` : ""
          }`,
        }));

      case "certifications":
        return data.map((cert, index) => ({
          key: index,
          label: `${cert.name || cert.title || `Certification ${index + 1}`}${
            cert.issueDate ? ` (${cert.issueDate})` : ""
          }`,
        }));

      case "skills":
        return data.map((skill, index) => ({
          key: index,
          label: typeof skill === "string" ? skill : `Skill ${index + 1}`,
        }));

      default:
        return data.map((item, index) => ({
          key: index,
          label: typeof item === "string" ? item : `Item ${index + 1}`,
        }));
    }
  };

  // Reset specific field when data field changes
  useEffect(() => {
    setSelectedSpecificField(null);
  }, [selectedDataField]);

  // Update selected resume data when field changes
  useEffect(() => {
    if (resumeData && resumeData.length > 0 && selectedDataField) {
      const sampleResume = resumeData[0];
      let fieldData = null;

      if (selectedDataField === "fullData") {
        fieldData = sampleResume;
      } else if (selectedDataField === "personalInfo") {
        // Handle personal info from parsedData
        fieldData = sampleResume.parsedData?.personalInfo || {
          name: sampleResume.userName || "No name available",
          email: sampleResume.userEmail || "No email available",
          phone: sampleResume.userPhone || "No phone available",
          location: sampleResume.location || "No location available",
          linkedIn: sampleResume.linkedIn || "No LinkedIn available",
          github: sampleResume.github || "No GitHub available",
        };
      } else if (selectedDataField === "summary") {
        fieldData = sampleResume.parsedData?.summary || "No summary available for this resume";
      } else if (selectedDataField === "experience") {
        const experienceData = sampleResume.parsedData?.experience || [];
        if (Array.isArray(experienceData) && experienceData.length === 0) {
          fieldData = { message: "No work experience data available for this resume" };
        } else if (selectedSpecificField !== null && experienceData[selectedSpecificField]) {
          // Return specific experience item
          fieldData = experienceData[selectedSpecificField];
        } else {
          fieldData = experienceData;
        }
      } else if (selectedDataField === "education") {
        const educationData = sampleResume.parsedData?.education || [];
        if (Array.isArray(educationData) && educationData.length === 0) {
          fieldData = { message: "No education data available for this resume" };
        } else if (selectedSpecificField !== null && educationData[selectedSpecificField]) {
          // Return specific education item
          fieldData = educationData[selectedSpecificField];
        } else {
          fieldData = educationData;
        }
      } else if (selectedDataField === "skills") {
        const skillsData = sampleResume.parsedData?.skills || [];
        if (Array.isArray(skillsData) && skillsData.length === 0) {
          fieldData = { message: "No skills data available for this resume" };
        } else if (selectedSpecificField !== null && skillsData[selectedSpecificField]) {
          // Return specific skill item
          fieldData = { skill: skillsData[selectedSpecificField] };
        } else {
          fieldData = skillsData;
        }
      } else if (selectedDataField === "projects") {
        const projectsData = sampleResume.parsedData?.projects || [];
        if (Array.isArray(projectsData) && projectsData.length === 0) {
          fieldData = { message: "No projects data available for this resume" };
        } else if (selectedSpecificField !== null && projectsData[selectedSpecificField]) {
          // Return specific project item
          fieldData = projectsData[selectedSpecificField];
        } else {
          fieldData = projectsData;
        }
      } else if (selectedDataField === "certifications") {
        const certificationsData = sampleResume.parsedData?.certifications || [];
        if (Array.isArray(certificationsData) && certificationsData.length === 0) {
          fieldData = { message: "No certifications data available for this resume" };
        } else if (selectedSpecificField !== null && certificationsData[selectedSpecificField]) {
          // Return specific certification item
          fieldData = certificationsData[selectedSpecificField];
        } else {
          fieldData = certificationsData;
        }
      } else if (selectedDataField === "languages") {
        fieldData = sampleResume.parsedData?.languages || [];
        if (Array.isArray(fieldData) && fieldData.length === 0) {
          fieldData = { message: "No languages data available for this resume" };
        }
      } else if (selectedDataField === "awards") {
        fieldData = sampleResume.parsedData?.awards || [];
        if (Array.isArray(fieldData) && fieldData.length === 0) {
          fieldData = { message: "No awards data available for this resume" };
        }
      } else if (selectedDataField === "publications") {
        fieldData = sampleResume.parsedData?.publications || [];
        if (Array.isArray(fieldData) && fieldData.length === 0) {
          fieldData = { message: "No publications data available for this resume" };
        }
      } else if (selectedDataField === "references") {
        fieldData = sampleResume.parsedData?.references || [];
        if (Array.isArray(fieldData) && fieldData.length === 0) {
          fieldData = { message: "No references data available for this resume" };
        }
      } else {
        // Generic fallback for any other fields
        fieldData = sampleResume.parsedData?.[selectedDataField] || {
          message: `No ${selectedDataField} data available for this resume`,
          availableFields: Object.keys(sampleResume.parsedData || {}),
          resumeId: sampleResume.resumeId || sampleResume._id,
          fileName: sampleResume.fileName,
        };
      }

      setSelectedResumeData(fieldData);
    } else {
      setSelectedResumeData({ message: "No resume data available" });
    }
  }, [selectedDataField, selectedSpecificField, resumeData]);

  // Parse variables in prompt text
  const parsePromptVariables = (text, resumeData) => {
    if (!text || !resumeData) return text;

    // Replace {resumeData.field} patterns
    return text.replace(/\{resumeData\.([^}]+)\}/g, (match, fieldPath) => {
      try {
        const fieldValue = getNestedValue(resumeData, fieldPath);
        return JSON.stringify(fieldValue, null, 2);
      } catch (error) {
        console.warn(`Could not parse variable: ${match}`, error);
        return match; // Return original if parsing fails
      }
    });
  };

  // Get nested value from object using dot notation
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  };

  // Handle test execution
  const handleTestPrompt = async () => {
    setTesting(true);
    setTestResult(null);
    setExecutionTime(null);
    setResponseRating(null);
    setGoodPoints([]);
    setBadPoints([]);

    const startTime = Date.now();

    try {
      // Parse variables in prompts
      const parsedSystemPrompt = parsePromptVariables(systemPrompt, selectedResumeData);
      const parsedUserPrompt = parsePromptVariables(userPrompt, selectedResumeData);

      const requestData = {
        provider: selectedProvider,
        model: selectedModel,
        systemPrompt: parsedSystemPrompt,
        userPrompt: parsedUserPrompt,
        temperature,
        maxTokens,
        selectedDataField,
        resumeData: selectedResumeData,
        includeAnalysis: true, // Request AI to analyze its own response
      };

      const response = await fetch.post("/resume/test-ai-prompt", requestData);

      const endTime = Date.now();
      const duration = endTime - startTime;
      setExecutionTime(duration);

      if (response.success) {
        setTestResult({
          success: true,
          response: response.data.response,
          usage: response.data.usage,
          model: response.data.model,
          duration,
        });

        // Extract AI analysis data if available from backend
        if (response.data.analysis) {
          setResponseRating(response.data.analysis.score);
          setGoodPoints(response.data.analysis.goodPoints);
          setBadPoints(response.data.analysis.badPoints);
        } else {
          // Parse analysis from AI response text
          const responseText = response.data.response;

          if (typeof responseText === "string") {
            // Extract score
            const scoreMatch = responseText.match(/Score:\s*(\d+)\/100/i);
            const score = scoreMatch ? parseInt(scoreMatch[1]) : null;

            // Extract good points with multiple pattern matching
            const goodPoints = [];
            let goodSectionMatch = responseText.match(/\*\*What's Good:\*\*([^]*?)(?=\*\*|$)/i);
            if (!goodSectionMatch) {
              goodSectionMatch = responseText.match(/What's Good:\s*([^]*?)(?=\*\*|$)/i);
            }
            if (!goodSectionMatch) {
              goodSectionMatch = responseText.match(/\*\*Strengths:\*\*([^]*?)(?=\*\*|$)/i);
            }
            if (!goodSectionMatch) {
              goodSectionMatch = responseText.match(/Strengths:\s*([^]*?)(?=\*\*|$)/i);
            }

            if (goodSectionMatch) {
              const content = goodSectionMatch[1];

              // Try multiple bullet point patterns
              let goodMatches = content.match(/[-•*]\s*(.+)/g);
              if (!goodMatches) {
                goodMatches = content.match(/\d+\.\s*(.+)/g); // Numbered lists
              }
              if (!goodMatches) {
                // Split by line breaks and filter non-empty lines
                goodMatches = content.split("\n").filter((line) => line.trim() && !line.match(/^\s*$/));
              }

              if (goodMatches) {
                goodMatches.forEach((match) => {
                  let point = match
                    .replace(/^[-•*]\s*/, "")
                    .replace(/^\d+\.\s*/, "")
                    .trim();
                  if (point && point.length > 3) {
                    // Only include meaningful points
                    goodPoints.push(point);
                  }
                });
              }
            }

            // Extract improvement points with multiple pattern matching
            const badPoints = [];
            let badSectionMatch = responseText.match(/\*\*What to Improve:\*\*([^]*?)(?=\*\*|$)/i);
            if (!badSectionMatch) {
              badSectionMatch = responseText.match(/What to Improve:\s*([^]*?)(?=\*\*|$)/i);
            }
            if (!badSectionMatch) {
              badSectionMatch = responseText.match(/\*\*Improvements:\*\*([^]*?)(?=\*\*|$)/i);
            }
            if (!badSectionMatch) {
              badSectionMatch = responseText.match(/Improvements:\s*([^]*?)(?=\*\*|$)/i);
            }

            if (badSectionMatch) {
              const content = badSectionMatch[1];

              // Try multiple bullet point patterns
              let badMatches = content.match(/[-•*]\s*(.+)/g);
              if (!badMatches) {
                badMatches = content.match(/\d+\.\s*(.+)/g); // Numbered lists
              }
              if (!badMatches) {
                // Split by line breaks and filter non-empty lines
                badMatches = content.split("\n").filter((line) => line.trim() && !line.match(/^\s*$/));
              }

              if (badMatches) {
                badMatches.forEach((match) => {
                  let point = match
                    .replace(/^[-•*]\s*/, "")
                    .replace(/^\d+\.\s*/, "")
                    .trim();
                  if (point && point.length > 3) {
                    // Only include meaningful points
                    badPoints.push(point);
                  }
                });
              }
            }

            // Extract AI suggestions with multiple pattern matching
            const suggestions = [];
            let suggestionsSectionMatch = responseText.match(/\*\*AI Suggestions:\*\*([^]*?)(?=\*\*|$)/i);
            if (!suggestionsSectionMatch) {
              suggestionsSectionMatch = responseText.match(/AI Suggestions:\s*([^]*?)(?=\*\*|$)/i);
            }
            if (!suggestionsSectionMatch) {
              suggestionsSectionMatch = responseText.match(/\*\*Suggestions:\*\*([^]*?)(?=\*\*|$)/i);
            }
            if (!suggestionsSectionMatch) {
              suggestionsSectionMatch = responseText.match(/Suggestions:\s*([^]*?)(?=\*\*|$)/i);
            }
            if (!suggestionsSectionMatch) {
              suggestionsSectionMatch = responseText.match(/\*\*Recommendations:\*\*([^]*?)(?=\*\*|$)/i);
            }

            if (suggestionsSectionMatch) {
              const content = suggestionsSectionMatch[1];

              // Try multiple bullet point patterns
              let suggestionMatches = content.match(/[-•*]\s*(.+)/g);
              if (!suggestionMatches) {
                suggestionMatches = content.match(/\d+\.\s*(.+)/g); // Numbered lists
              }
              if (!suggestionMatches) {
                // Split by line breaks and filter non-empty lines
                suggestionMatches = content.split("\n").filter((line) => line.trim() && !line.match(/^\s*$/));
              }

              if (suggestionMatches) {
                suggestionMatches.forEach((match) => {
                  let suggestion = match
                    .replace(/^[-•*]\s*/, "")
                    .replace(/^\d+\.\s*/, "")
                    .trim();
                  if (suggestion && suggestion.length > 3) {
                    // Only include meaningful suggestions
                    suggestions.push(suggestion);
                  }
                });
              }
            }

            if (score !== null) {
              setResponseRating(score);
              setGoodPoints(goodPoints);
              setBadPoints(badPoints);
              setSuggestions(suggestions);
            }
          }
        }

        message.success(`Prompt executed successfully in ${duration}ms`);
      } else {
        setTestResult({
          success: false,
          error: response.message || "Unknown error occurred",
          duration,
        });

        // No analysis for failed requests
        setResponseRating(null);
        setGoodPoints([]);
        setBadPoints([]);
        setSuggestions([]);

        message.error("Prompt execution failed");
      }
    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;
      setExecutionTime(duration);

      setTestResult({
        success: false,
        error: error.message || "Network error occurred",
        duration,
      });

      // No analysis for network errors
      setResponseRating(null);
      setGoodPoints([]);
      setBadPoints([]);
      setSuggestions([]);

      message.error("Failed to execute prompt");
    } finally {
      setTesting(false);
    }
  };

  // Handle modal reset
  const handleReset = () => {
    setSystemPrompt("");
    setUserPrompt("");
    setSelectedProvider("openai");
    setSelectedModel("gpt-4o-mini");
    setTemperature(0.3);
    setMaxTokens(4000);
    setSelectedDataField("personalInfo");
    setSelectedSpecificField(null);
    setTestResult(null);
    setExecutionTime(null);
    setSelectedResumeData(null);
    setResponseRating(null);
    setGoodPoints([]);
    setBadPoints([]);
    setSuggestions([]);
    form.resetFields();
  };

  // Copy to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    message.success(`${type} copied to clipboard!`);
  };

  // Extract only the actual changes/updates from AI response (not the analysis)
  const extractChangesOnly = (response) => {
    if (!response) return "No response available";

    let text = typeof response === "string" ? response : JSON.stringify(response, null, 2);

    // Remove markdown code blocks if present
    text = text.replace(/```json\s*\n?/gi, "").replace(/```\s*$/g, "");
    text = text.replace(/^```\s*\n?/g, "").replace(/```\s*$/g, "");

    const trimmedText = text.trim();

    // Try to parse as JSON first
    if (trimmedText.includes("{") && trimmedText.includes("}")) {
      try {
        let cleanText = trimmedText;
        const firstBrace = cleanText.indexOf("{");
        if (firstBrace > 0) {
          cleanText = cleanText.substring(firstBrace);
        }
        const lastBrace = cleanText.lastIndexOf("}");
        if (lastBrace !== -1 && lastBrace < cleanText.length - 1) {
          cleanText = cleanText.substring(0, lastBrace + 1);
        }

        const parsed = JSON.parse(cleanText);
        if (typeof parsed === "object" && parsed !== null) {
          // For JSON responses, show ONLY the actual content changes (enhanced_summary)
          // Remove all analysis fields
          const filteredData = {};

          // Keep only the actual changes/content
          if (parsed.enhanced_summary) {
            filteredData.enhanced_summary = parsed.enhanced_summary;
          }

          // Remove analysis fields completely
          // delete filteredData.whats_good;
          // delete filteredData.what_can_be_a_problem;
          // delete filteredData.score;
          // delete filteredData.improvement_feedback;

          // If we have changes, show them
          if (Object.keys(filteredData).length > 0) {
            if (filteredData.enhanced_summary) {
              // Just return the enhanced summary as plain text
              return filteredData.enhanced_summary.replace(/\n/g, "<br/>");
            }
            return formatAsResumeDataTable(filteredData);
          }

          // If no changes found, return message
          return "No content changes detected - only analysis data returned.";
        }
      } catch (parseError) {
        // Fall back to text processing
      }
    }

    // Remove analysis sections from text format
    let cleanText = trimmedText;

    // Remove the entire Analysis section (everything from **Analysis:** onwards)
    cleanText = cleanText.replace(/\*\*Analysis:\*\*[\s\S]*$/gi, "");
    cleanText = cleanText.replace(/Analysis:[\s\S]*$/gi, "");

    // Remove all analysis-related sections individually
    const sectionsToRemove = [
      // Score sections
      /\*\*Score:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /Score:\s*\d+\/100[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /\*\*Overall Score:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,

      // What's Good / Strengths sections
      /\*\*What's Good:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /What's Good:[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /\*\*Strengths:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /Strengths:[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,

      // What Can Be a Problem / Improvements sections
      /\*\*What Can Be a Problem:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /What Can Be a Problem:[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /\*\*What to Improve:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /What to Improve:[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /\*\*Improvements:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /Improvements:[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,

      // Improvement Feedback sections
      /\*\*Improvement Feedback:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /Improvement Feedback:[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,

      // AI Suggestions sections
      /\*\*AI Suggestions:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /AI Suggestions:[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /\*\*Suggestions:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /Suggestions:[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
      /\*\*Recommendations:\*\*[^]*?(?=\*\*[A-Za-z]|\n[A-Z][a-z]|$)/gi,
    ];

    sectionsToRemove.forEach((regex, index) => {
      const before = cleanText;
      cleanText = cleanText.replace(regex, "");
      if (before !== cleanText) {
      }
    });

    // Clean up extra whitespace and line breaks
    cleanText = cleanText.replace(/\*\*\s*$/gm, ""); // Remove trailing **
    cleanText = cleanText.replace(/^\s*\*\*\s*$/gm, ""); // Remove lines with just **
    cleanText = cleanText.replace(/\n\s*\n\s*\n/g, "\n\n").trim();

    // If we removed everything, return original
    if (!cleanText || cleanText.length < 10) {
      const fallback = text.replace(/\n/g, "<br/>");
      return fallback;
    }

    const result = cleanText.replace(/\n/g, "<br/>");
    return result;
  };

  // Format AI response like the resume data table format
  const formatAIResponse = (response) => {
    if (!response) return "No response available";

    let text = typeof response === "string" ? response : JSON.stringify(response, null, 2);

    // Remove markdown code blocks if present (```json ... ```)
    text = text.replace(/```json\s*\n?/gi, "").replace(/```\s*$/g, "");
    text = text.replace(/^```\s*\n?/g, "").replace(/```\s*$/g, "");

    const trimmedText = text.trim();

    // Very aggressive JSON detection - look for any JSON structure
    if (trimmedText.includes("{") && trimmedText.includes("}")) {
      try {
        // Extract JSON from the text - find the outermost braces
        let cleanText = trimmedText;

        // Remove any leading text before the first {
        const firstBrace = cleanText.indexOf("{");
        if (firstBrace > 0) {
          cleanText = cleanText.substring(firstBrace);
        }

        // Remove any trailing text after the last }
        const lastBrace = cleanText.lastIndexOf("}");
        if (lastBrace !== -1 && lastBrace < cleanText.length - 1) {
          cleanText = cleanText.substring(0, lastBrace + 1);
        }

        // Try to parse the cleaned JSON
        const parsed = JSON.parse(cleanText);
        if (typeof parsed === "object" && parsed !== null) {
          return formatAsResumeDataTable(parsed);
        }
      } catch (parseError) {
        // If parsing fails, try to extract individual JSON objects
        const jsonMatches = trimmedText.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g);
        if (jsonMatches && jsonMatches.length > 0) {
          try {
            const parsed = JSON.parse(jsonMatches[0]);
            if (typeof parsed === "object" && parsed !== null) {
              return formatAsResumeDataTable(parsed);
            }
          } catch (innerError) {}
        }
      }
    }

    // If not JSON, format as plain text with line breaks
    return text.replace(/\n/g, "<br/>");
  };

  // Format object exactly like the resume data table
  const formatAsResumeDataTable = (obj, depth = 0) => {
    if (!obj || typeof obj !== "object") {
      return String(obj || "null");
    }

    if (Array.isArray(obj)) {
      return obj
        .map((item, index) => {
          if (typeof item === "object") {
            return `<div style="margin: 8px 0; padding: 8px; background: #f9f9f9; border-radius: 4px;">
            <span style="color: #000; font-weight: 500;">Item ${index + 1}:</span>
            <div style="margin-left: 16px; margin-top: 4px;">
              ${formatAsResumeDataTable(item, depth + 1)}
            </div>
          </div>`;
          } else {
            return `<div style="margin: 4px 0;">
            <span style="color: #000; font-weight: 500;">•</span> 
            <span style="color: #000; margin-left: 4px;">${String(item)}</span>
          </div>`;
          }
        })
        .join("");
    }

    const items = Object.entries(obj).map(([key, value]) => {
      // Format key exactly like resume data table: convert to Title Case with ":"
      const formattedKey = key
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/\b\w/g, (l) => l.toUpperCase())
        .trim();

      if (value === null || value === undefined) {
        return `<div style="margin: 4px 0;">
          <span style="color: #000; font-weight: 500;">${formattedKey}: </span>
          <span style="color: #000;">null</span>
        </div>`;
      } else if (typeof value === "object" && !Array.isArray(value)) {
        return `<div style="margin: 8px 0;">
          <div style="color: #000; font-weight: 500; margin-bottom: 4px;">${formattedKey}:</div>
          <div style="margin-left: 16px; padding: 8px; background: #f9f9f9; border-radius: 4px;">
            ${formatAsResumeDataTable(value, depth + 1)}
          </div>
        </div>`;
      } else if (Array.isArray(value)) {
        if (value.length === 0) {
          return `<div style="margin: 4px 0;">
            <span style="color: #000; font-weight: 500;">${formattedKey}: </span>
            <span style="color: #000;">null</span>
          </div>`;
        }
        return `<div style="margin: 8px 0;">
          <div style="color: #000; font-weight: 500; margin-bottom: 4px;">${formattedKey}:</div>
          <div style="margin-left: 16px;">
            ${formatAsResumeDataTable(value, depth + 1)}
          </div>
        </div>`;
      } else {
        // Format exactly like resume table: "Key: Value"
        const displayValue = typeof value === "boolean" ? (value ? "true" : "false") : String(value);
        return `<div style="margin: 4px 0;">
          <span style="color: #000; font-weight: 500;">${formattedKey}: </span>
          <span style="color: #000;">${displayValue}</span>
        </div>`;
      }
    });

    return items.join("");
  };

  // Get current models for selected provider
  const getCurrentModels = () => {
    return AI_PROVIDERS[selectedProvider]?.models || [];
  };

  // Format JSON for display
  const formatJSON = (data) => {
    if (!data) return "No data available";
    try {
      if (typeof data === "string") return data;
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return "Error formatting data";
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SendOutlined style={{ color: "#52c41a" }} />
          <Title level={4} style={{ margin: 0 }}>
            AI Prompt Testing Lab
          </Title>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
      style={{ top: 20 }}
      bodyStyle={{ padding: "24px", maxHeight: "80vh", overflowY: "auto" }}
    >
      <Row gutter={[24, 24]}>
        {/* Left Column - Configuration */}
        <Col span={12}>
          <Card title="Prompt Configuration" size="small">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {/* System Prompt */}
              <div>
                <Text strong style={{ fontSize: "14px" }}>
                  System Prompt:
                </Text>
                <TextArea
                  placeholder="Enter system prompt here... Use {resumeData.fieldName} for dynamic variables"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={15}
                  size="large"
                  style={{ marginTop: 8, fontSize: "13px", minHeight: "300px" }}
                />
              </div>

              {/* User Prompt */}
              <div>
                <Text strong style={{ fontSize: "14px" }}>
                  User Prompt:
                </Text>
                <TextArea
                  placeholder="Enter user prompt here... Use {resumeData.fieldName} for dynamic variables"
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  rows={4}
                  size="large"
                  style={{ marginTop: 8, fontSize: "13px" }}
                />
              </div>

              {/* Model Selection */}
              <Row gutter={12}>
                <Col span={12}>
                  <Text strong style={{ fontSize: "14px" }}>
                    AI Provider:
                  </Text>
                  <Select
                    value={selectedProvider}
                    onChange={setSelectedProvider}
                    style={{ width: "100%", marginTop: 8 }}
                    size="large"
                    placeholder="Select AI Provider"
                    dropdownStyle={{ minWidth: "300px" }}
                  >
                    {Object.entries(AI_PROVIDERS).map(([key, provider]) => (
                      <Option key={key} value={key}>
                        <div style={{ fontSize: "14px", fontWeight: "500" }}>{provider.name}</div>
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <Text strong style={{ fontSize: "14px" }}>
                    Model:
                  </Text>
                  <Select
                    value={selectedModel}
                    onChange={setSelectedModel}
                    style={{ width: "100%", marginTop: 8 }}
                    size="large"
                    placeholder="Select Model"
                    dropdownStyle={{ minWidth: "350px" }}
                    optionLabelProp="label"
                  >
                    {getCurrentModels().map((model) => (
                      <Option key={model.id} value={model.id} label={model.name}>
                        <div
                          style={{
                            padding: "4px 0",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            maxWidth: "330px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              marginBottom: "2px",
                            }}
                          >
                            {model.name}
                          </div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: "12px",
                              lineHeight: "1.4",
                              display: "block",
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                            }}
                          >
                            {model.description}
                          </Text>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              {/* Parameters */}
              <Row gutter={12}>
                <Col span={12}>
                  <Text strong style={{ fontSize: "14px" }}>
                    Temperature: {temperature}
                  </Text>
                  <Slider
                    min={0}
                    max={2}
                    step={0.1}
                    value={temperature}
                    onChange={setTemperature}
                    style={{ marginTop: 8 }}
                    tooltip={{ formatter: (value) => `${value}` }}
                  />
                </Col>
                <Col span={12}>
                  <Text strong style={{ fontSize: "14px" }}>
                    Max Tokens:
                  </Text>
                  <InputNumber
                    min={1}
                    max={4000}
                    value={maxTokens}
                    onChange={setMaxTokens}
                    size="large"
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </Col>
              </Row>
            </Space>
          </Card>

          {/* Action Buttons */}
          <Card title="Actions" size="small" style={{ marginTop: 16 }}>
            <Space>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleTestPrompt}
                loading={testing}
                disabled={!systemPrompt || !userPrompt}
              >
                Test Prompt
              </Button>
              <Button icon={<ClearOutlined />} onClick={handleReset}>
                Reset
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Right Column - Data & Results */}
        <Col span={12}>
          {/* Resume Data Field Selection */}
          <Card title="Resume Data Selection" size="small">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Text strong style={{ fontSize: "14px" }}>
                  Data Field:
                </Text>
                <Select
                  value={selectedDataField}
                  onChange={setSelectedDataField}
                  style={{ width: "100%", marginTop: 8 }}
                  size="large"
                  placeholder="Select resume data field"
                  dropdownStyle={{ minWidth: "400px" }}
                  optionLabelProp="label"
                >
                  {RESUME_DATA_FIELDS.map((field) => (
                    <Option key={field.id} value={field.id} label={field.name}>
                      <div
                        style={{
                          padding: "4px 0",
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          maxWidth: "380px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "2px",
                          }}
                        >
                          {field.name}
                        </div>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: "12px",
                            lineHeight: "1.4",
                            display: "block",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        >
                          {field.description}
                        </Text>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Specific Field Selection - Show only for array fields */}
              {(() => {
                if (!resumeData || resumeData.length === 0) return null;
                const sampleResume = resumeData[0];
                const fieldData = sampleResume.parsedData?.[selectedDataField];
                const specificFields = getSpecificFields(selectedDataField, fieldData);

                if (!specificFields) return null;

                return (
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      Specific Item:
                    </Text>
                    <Select
                      value={selectedSpecificField}
                      onChange={setSelectedSpecificField}
                      style={{ width: "100%", marginTop: 8 }}
                      size="large"
                      placeholder={`Select specific ${selectedDataField.replace(/s$/, "")} item`}
                      dropdownStyle={{ minWidth: "500px", maxWidth: "600px" }}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      optionLabelProp="title"
                    >
                      {specificFields.map((field) => (
                        <Option key={field.key} value={field.key} title={field.label}>
                          <div
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              maxWidth: "480px",
                              lineHeight: "1.4",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "500",
                                color: "#333",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              {field.label}
                            </div>
                          </div>
                        </Option>
                      ))}
                    </Select>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: "12px",
                        display: "block",
                        marginTop: "4px",
                      }}
                    >
                      Leave empty to use all {selectedDataField} data, or select specific item for targeted testing
                    </Text>
                  </div>
                );
              })()}

              {/* Available Fields Info */}
              {resumeData && resumeData.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "12px", color: "#666" }}>
                    Available fields:{" "}
                    {Object.keys(resumeData[0].parsedData || {}).join(", ") || "No parsed data available"}
                  </Text>
                  <br />
                  <Text style={{ fontSize: "11px", color: "#999" }}>
                    Resume ID: {resumeData[0].resumeId} | File: {resumeData[0].fileName}
                  </Text>
                </div>
              )}

              {/* Selected Data Preview */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong>Selected Data Preview:</Text>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(formatJSON(selectedResumeData), "Resume data")}
                    disabled={!selectedResumeData}
                  >
                    Copy
                  </Button>
                </div>
                <div
                  style={{
                    background:
                      selectedResumeData?.message && selectedResumeData.message.includes("No")
                        ? "#fff2f0"
                        : "#f5f5f5",
                    border:
                      selectedResumeData?.message && selectedResumeData.message.includes("No")
                        ? "1px solid #ffccc7"
                        : "1px solid #d9d9d9",
                    padding: "12px",
                    borderRadius: "6px",
                    marginTop: 8,
                    maxHeight: "300px",
                    overflowY: "auto",
                    fontSize: "12px",
                    fontFamily: "monospace",
                  }}
                >
                  {selectedResumeData ? (
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{formatJSON(selectedResumeData)}</pre>
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#999",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      <Text type="secondary">Loading resume data...</Text>
                    </div>
                  )}
                </div>
              </div>
            </Space>
          </Card>

          {/* Test Results */}
          <Card title="Test Results" size="small" style={{ marginTop: 16 }}>
            {testing && (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text>Testing AI prompt...</Text>
                </div>
              </div>
            )}

            {testResult && (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {/* Status and Timing */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    {testResult.success ? (
                      <Tag color="success" icon={<CheckCircleOutlined />}>
                        Success
                      </Tag>
                    ) : (
                      <Tag color="error" icon={<ExclamationCircleOutlined />}>
                        Failed
                      </Tag>
                    )}
                  </div>
                  <div>
                    <Tag icon={<ClockCircleOutlined />}>
                      {testResult.duration >= 1000
                        ? `${(testResult.duration / 1000).toFixed(1)}s`
                        : `${testResult.duration}ms`}
                    </Tag>
                  </div>
                </div>

                {/* Response or Error */}
                {testResult.success ? (
                  <div>
                    {/* AI Response - Only Changes/Updates */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Text strong>AI Response (Changes Only):</Text>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          let rawText =
                            typeof testResult.response === "string"
                              ? testResult.response
                              : JSON.stringify(testResult.response, null, 2);

                          // Clean up any JSON formatting if it exists for copying
                          try {
                            if (rawText.trim().startsWith("{") || rawText.trim().startsWith("[")) {
                              const parsed = JSON.parse(rawText);
                              if (parsed.text) rawText = parsed.text;
                              else if (parsed.content) rawText = parsed.content;
                              else if (parsed.message) rawText = parsed.message;
                              else if (Array.isArray(parsed)) rawText = parsed.join("\n");
                            }
                          } catch (e) {
                            // Keep original if parsing fails
                          }

                          copyToClipboard(rawText, "AI response");
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <div
                      style={{
                        background: "#f6ffed",
                        border: "1px solid #b7eb8f",
                        padding: "16px",
                        borderRadius: "8px",
                        marginTop: 8,
                        maxHeight: "300px",
                        overflowY: "auto",
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        fontSize: "14px",
                        lineHeight: "1.6",
                        color: "#2c3e50",
                      }}
                    >
                      <div
                        style={{
                          wordBreak: "break-word",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: extractChangesOnly(testResult.response),
                        }}
                      />
                    </div>

                    {/* Analysis Section - Score, Strengths, Improvements, AI Suggestions */}
                    <div style={{ marginTop: 16 }}>
                      <Text strong style={{ fontSize: "16px", marginBottom: "8px", display: "block" }}>
                        Response Quality Analysis
                      </Text>
                      <AnalysisDisplay
                        score={responseRating}
                        goodPoints={goodPoints}
                        badPoints={badPoints}
                        suggestions={suggestions}
                      />
                    </div>

                    {/* Usage Statistics */}
                    {testResult.usage && (
                      <div style={{ marginTop: 12 }}>
                        <Text strong>Token Usage:</Text>
                        <div style={{ marginTop: 8 }}>
                          <Tag>Input: {testResult.usage.prompt_tokens || 0}</Tag>
                          <Tag>Output: {testResult.usage.completion_tokens || 0}</Tag>
                          <Tag>Total: {testResult.usage.total_tokens || 0}</Tag>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Alert message="Test Failed" description={testResult.error} type="error" showIcon />
                )}
              </Space>
            )}

            {!testing && !testResult && (
              <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                <Text type="secondary">
                  No test results yet. Configure your prompt and click "Test Prompt" to begin.
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default AIPromptTestingModal;
